import db from '../../data/db';

const { useState, useEffect } = wp.element;

/**
 * Retrieves the translated header name from the pre-translated headers.
 *
 * This function assumes that the header translations are already handled in PHP and passed
 * to JavaScript. It looks up the header in the TABLE object, which contains
 * pre-translated headers for each country code.
 *
 * @param { string } header      - The key of the header to translate.
 * @param { string } countryCode - String containing block defined country.
 *
 * @return { string } The translated header name if found, otherwise returns the original header key.
 */
const translateHeader = ( header, countryCode ) => {
	const headers = AcProductLookupBlock.table[ countryCode ];
	const headerInfo = headers.find( h => h.key === header );
	return headerInfo ? headerInfo.text : header;
};

/**
 * Fetches headers from the database and updates the state.
 *
 * @param { string }   countryCode - The country code for the headers table.
 * @param { Function } setHeaders  - The state setter function for headers.
 */
const fetchHeaders = async ( countryCode, setHeaders ) => {
	const dbHeaders = await db.table( `${ countryCode }_headers` ).toArray();
	if ( dbHeaders ) {
		setHeaders( dbHeaders.map( header => header.name ) );
	}
};

/**
 * Renders the data table for the Product - Lookup Block.
 *
 * @param { Object } data             - Object containing an array of product data to display.
 * @param { Array }  data.data        - Array of product data to display.
 * @param { string } data.countryCode - String containing block defined country.
 *
 * @return { JSX.Element } Rendered data table.
 */
const DataTable = ( { data, countryCode } ) => {
	const [ headers, setHeaders ] = useState( [ ] );

	useEffect( () => {
		fetchHeaders( countryCode, setHeaders );
	}, [] );

	return (
		<div aria-live="polite">
			<table className="product-lookup__data-table">
				<thead>
					<tr className="product-lookup__data-table-row product-lookup__data-table-row--header">
						{ headers.map( ( header, index ) => (
							<th key={ index } className="product-lookup__data-table-cell product-lookup__data-table-cell--header">
								{ translateHeader( header, countryCode ) }
							</th>
						) ) }
					</tr>
				</thead>
				<tbody>
					{ data.map( ( item, index ) => (
						<tr className="product-lookup__data-table-row" key={ index }>
							{ headers.map( ( header, idx ) => (
								<td key={ idx } className="product-lookup__data-table-cell">
									<span className="product-lookup__data-table-cell--title" aria-hidden="true"> { translateHeader( header, countryCode ) }</span>
									{ item[ header ] }
								</td>
							) ) }
						</tr>
					) ) }
				</tbody>
			</table>
		</div>
	);
};

export default DataTable;
